import React, { useState } from "react";
import {
  TextField,
  Box,
  Typography,
  Chip,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Add, ExpandMore, Close, Edit } from "@mui/icons-material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import "./EditExperience.css"; // Make sure to create and name this CSS file
import { useDispatch, useSelector } from "react-redux";
import {
  scrollToSpecificContentAction,
  setProfileState,
} from "../../../actions/MyTechProfile/myTechProfileActions";
import { allSkills } from "../../../utils/constants";

// --- Custom Theme ---
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#ffd700", // Purple for buttons and highlights
    },
    background: {
      paper: "#1e1e1e", // Background for Autocomplete, Accordion
      default: "#000000",
    },
    text: {
      primary: "#ffffff",
      secondary: "#b0b0b0",
    },
  },
});

// --- The Main Component ---
const EditExperiences = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.editMyTechProfileStore);
  const [experiences, setExperiences] = useState(profile?.experiences || []);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const initialFormState = {
    experience_id: "",
    logo: "",
    company: "",
    title: "",
    startDate: null,
    endDate: null,
    details: "",
    techStack: [],
  };

  const [currentExperience, setCurrentExperience] = useState(initialFormState);
  const [isEndDatePresent, setIsEndDatePresent] = useState(false);

  const handleSave = () => {
    if (!currentExperience?.company || !currentExperience?.title) return;

    let updatedExperiences;

    const experienceToSave = {
      ...currentExperience,
      startDate: currentExperience.startDate
        ? dayjs(currentExperience.startDate).format("MMMM YYYY")
        : null,
      endDate: isEndDatePresent
        ? "Present"
        : currentExperience.endDate
        ? dayjs(currentExperience.endDate).format("MMMM YYYY")
        : null,
    };

    if (editingId !== null) {
      // Update existing experience
      updatedExperiences = experiences?.map((exp) =>
        exp?.experience_id === editingId ? experienceToSave : exp
      );
    } else {
      // Add new experience
      const newExperience = {
        ...experienceToSave,
        experience_id: Date.now(),
      };
      updatedExperiences = [...experiences, newExperience];
    }

    setExperiences(updatedExperiences);
    dispatch(setProfileState({ ...profile, experiences: updatedExperiences }));

    // Reset and close form
    setIsFormVisible(false);
    setEditingId(null);
    setCurrentExperience(initialFormState);
    setIsEndDatePresent(false);
    dispatch(scrollToSpecificContentAction("experiences"));
  };

  const handleAddNew = () => {
    setEditingId(null);
    setCurrentExperience(initialFormState);
    setIsFormVisible(true);
    setIsEndDatePresent(false);
    dispatch(scrollToSpecificContentAction("experiences"));
  };

  const handleEdit = (experience) => {
    setEditingId(experience?.experience_id);
    const isPresent = experience.endDate === "Present";
    setIsEndDatePresent(isPresent);
    setCurrentExperience({
      ...experience,
      startDate: experience.startDate ? dayjs(experience.startDate) : null,
      endDate: isPresent
        ? null
        : experience.endDate
        ? dayjs(experience.endDate)
        : null,
    });
    setIsFormVisible(true);
  };

  const handleCancel = () => {
    setIsFormVisible(false);
    setEditingId(null);
    setCurrentExperience(initialFormState);
    setIsEndDatePresent(false);
    dispatch(scrollToSpecificContentAction("experiences"));
  };

  const handleExperienceRemove = (expId) => {
    const updatedExperiences = experiences.filter(
      (exp) => exp?.experience_id !== expId
    );
    setExperiences(updatedExperiences);
    dispatch(
      setProfileState({
        ...profile,
        experiences: updatedExperiences,
      })
    );
  };

  const handlePresentCheckbox = (event) => {
    setIsEndDatePresent(event.target.checked);
    if (event.target.checked) {
      setCurrentExperience({ ...currentExperience, endDate: null });
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <div className="edit-experience-container">
          <Typography className="edit-experience-title">
            Edit Experiences
          </Typography>

          <div className="experiences-list">
            {experiences?.map((exp) => (
              <Accordion
                key={exp.experience_id}
                className="experience-accordion"
                onChange={() =>
                  dispatch(scrollToSpecificContentAction("experiences"))
                }
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: "#fff" }} />}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    {/* Action Icons */}
                    <Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExperienceRemove(exp?.experience_id);
                        }}
                        className="remove-exp-btn"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(exp);
                        }}
                        className="edit-exp-btn"
                        sx={{ color: "#aaa", ml: 0.5 }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Experience Info */}
                    <Box sx={{ ml: 1, display: "flex", alignItems: "center" }}>
                      <img
                        src={exp?.logo}
                        alt={exp?.company}
                        className="company-logo-summary"
                      />
                      <Box sx={{ ml: 2 }}>
                        <Typography>{exp?.title}</Typography>
                        <Typography variant="caption" sx={{ color: "#bbbbbb" }}>
                          {exp?.company}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {exp.details}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {exp.techStack.map((skill) => {
                      return (
                        <Chip
                          key={skill.id}
                          label={skill.name}
                          size="small"
                          className="tech-stack-chip"
                        />
                      );
                    })}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
          </div>

          {!isFormVisible ? (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAddNew}
              fullWidth
              sx={{
                fontFamily: "Questrial",
                textTransform: "inherit",
                fontSize: "16px",
                color: "black",
                backgroundColor: "#ffd700 ",
                "&:hover": {
                  backgroundColor: "#ffd700",
                },
              }}
            >
              Add Experience
            </Button>
          ) : (
            <Box className="add-experience-form">
              <Typography variant="h6" sx={{ mb: 2 }}>
                {editingId !== null ? "Edit Experience" : "Add New Experience"}
              </Typography>

              {/* Company Logo URL */}
              <Typography variant="subtitle1" className="edit-experience-label">
                Company Logo URL
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="https://example.com/logo.png"
                value={currentExperience?.logo}
                onChange={(e) =>
                  setCurrentExperience({
                    ...currentExperience,
                    logo: e.target.value,
                  })
                }
                onFocus={() =>
                  dispatch(scrollToSpecificContentAction("experiences"))
                }
                sx={{ mb: 0.5 }}
              />
              <Typography variant="caption" className="logo-url-note">
                Upload your company logo to Google Drive (or any cloud storage)
                and paste the shareable link here, or directly provide the
                logo’s URL.
              </Typography>

              {/* Company Name & Title */}
              <div
                style={{
                  display: "flex",
                  gap: "20px",
                  marginBottom: "16px",
                  marginTop: "16px",
                }}
              >
                <div style={{ width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    className="edit-experience-label"
                  >
                    Company Name
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="e.g., Tech Solutions Inc."
                    value={currentExperience?.company}
                    onChange={(e) =>
                      setCurrentExperience({
                        ...currentExperience,
                        company: e.target.value,
                      })
                    }
                    onFocus={() =>
                      dispatch(scrollToSpecificContentAction("experiences"))
                    }
                  />
                </div>
                <div style={{ width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    className="edit-experience-label"
                  >
                    Title / Role
                  </Typography>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="e.g., Software Engineer"
                    value={currentExperience.title}
                    onChange={(e) =>
                      setCurrentExperience({
                        ...currentExperience,
                        title: e.target.value,
                      })
                    }
                    onFocus={() =>
                      dispatch(scrollToSpecificContentAction("experiences"))
                    }
                  />
                </div>
              </div>

              {/* Start & End Date */}
              <div
                style={{ display: "flex", gap: "20px", marginBottom: "16px" }}
              >
                <div style={{ width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    className="edit-experience-label"
                  >
                    Start Date
                  </Typography>
                  <DatePicker
                    views={["year", "month"]}
                    value={currentExperience.startDate}
                    onChange={(newValue) => {
                      setCurrentExperience({
                        ...currentExperience,
                        startDate: newValue,
                      });
                      dispatch(scrollToSpecificContentAction("experiences"));
                    }}
                    renderInput={(params) => (
                      <TextField
                        onFocus={() =>
                          dispatch(scrollToSpecificContentAction("experiences"))
                        }
                        {...params}
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                </div>
                <div style={{ width: "100%" }}>
                  <Typography
                    variant="subtitle1"
                    className="edit-experience-label"
                  >
                    End Date
                  </Typography>
                  <DatePicker
                    views={["year", "month"]}
                    value={currentExperience.endDate}
                    disabled={isEndDatePresent}
                    minDate={currentExperience.startDate || null}
                    onChange={(newValue) => {
                      setCurrentExperience({
                        ...currentExperience,
                        endDate: newValue,
                      });
                      dispatch(scrollToSpecificContentAction("experiences"));
                    }}
                    onFocus={() =>
                      dispatch(scrollToSpecificContentAction("experiences"))
                    }
                    shouldDisableDate={(date) =>
                      currentExperience.startDate
                        ? date.isBefore(currentExperience.startDate, "day")
                        : false
                    }
                    renderInput={(params) => (
                      <TextField
                        onFocus={() =>
                          dispatch(scrollToSpecificContentAction("experiences"))
                        }
                        {...params}
                        size="small"
                        fullWidth
                      />
                    )}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isEndDatePresent}
                        onChange={handlePresentCheckbox}
                        size="small"
                        onFocus={() =>
                          dispatch(scrollToSpecificContentAction("experiences"))
                        }
                      />
                    }
                    label={<Typography variant="caption">Present</Typography>}
                  />
                </div>
              </div>

              {/* Details */}
              <Typography variant="subtitle1" className="edit-experience-label">
                Details
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                multiline
                rows={4}
                placeholder="Describe your responsibilities and achievements..."
                value={currentExperience.details}
                onChange={(e) =>
                  setCurrentExperience({
                    ...currentExperience,
                    details: e.target.value,
                  })
                }
                onFocus={() =>
                  dispatch(scrollToSpecificContentAction("experiences"))
                }
                sx={{ mb: 2 }}
              />

              {/* Tech Stack */}
              <Typography variant="subtitle1" className="edit-experience-label">
                Tech Stack
              </Typography>
              <Autocomplete
                multiple
                options={allSkills}
                getOptionLabel={(option) => option.name}
                value={currentExperience?.techStack}
                onChange={(event, value) => {
                  setCurrentExperience({
                    ...currentExperience,
                    techStack: value,
                  });
                  dispatch(scrollToSpecificContentAction("experiences"));
                }}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option?.name}
                      size="small"
                      {...getTagProps({ index })}
                      className="tech-stack-chip"
                      sx={{
                        margin: "2px",
                        "& .MuiChip-deleteIcon": {
                          color: "black",
                          opacity: 0.9,
                        },
                        "&:hover .MuiChip-deleteIcon": {
                          color: "black",
                          opacity: 1,
                        },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select skills used"
                  />
                )}
                sx={{ mb: 2 }}
                onFocus={() =>
                  dispatch(scrollToSpecificContentAction("experiences"))
                }
              />

              {/* Action Buttons */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <Button
                  sx={{
                    fontFamily: "Questrial",
                    color: "#fff",
                    textTransform: "inherit",
                  }}
                  onClick={handleCancel}
                  size="small"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  variant="contained"
                  size="small"
                  sx={{
                    ml: 1,
                    fontFamily: "Questrial",
                    textTransform: "inherit",
                  }}
                >
                  Save
                </Button>
              </Box>
            </Box>
          )}
        </div>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default EditExperiences;
